import React, { useState, useEffect } from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { useNavigate, Outlet } from 'react-router-dom'
import settingsService from '../../services/settingsService'
import { toast } from 'react-toastify'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  Shield,
  Database,
  MessageSquare,
  FileText,
  Briefcase,
  Settings as SettingsIcon,
  User,
  Globe,
  Lock,
  ShoppingCart,
  UserCheck,
  Lightbulb,
  Upload,
  Package,
  Factory,
  Award,
  Tag,
  Wrench,
  Cog,
  CreditCard,
  Calculator,
  Hash,
  Sliders,
  FileSearch,
  Mail,
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
// Consolidated CSS is imported globally'

const Settings = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [selectedRows, setSelectedRows] = useState([])
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const formatTimeAgo = (date) => {
    if (!date) return ''
    const now = new Date()
    const past = new Date(date)
    const diffInSeconds = Math.floor((now - past) / 1000)
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return past.toLocaleDateString()
  }

  // Load dashboard data for overview and hub card counts
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true)
        const response = await settingsService.getDashboardData()
        
        if (response.success && response.data) {
          // Ensure users array exists and has proper structure
          const processedData = {
            ...response.data,
            users: response.data.users?.map(user => ({
              userName: user.name || user.userName || 'Unknown User',
              userType: user.userType || 'Internal',
              role: user.role || 'User',
              email: user.email || user.userEmail || 'No email',
              status: user.status || 'inactive',
              isOnline: user.isOnline || false,
              lastLogin: user.lastLogin || user.lastLoggedIn || 'Never',
              ipAddress: user.ipAddress || user.userIP || 'N/A',
              device: user.device || user.userDevice || 'Unknown',
              location: user.location || user.userLocation || 'Unknown'
            })) || []
          }
          setDashboardData(processedData)
        } else {
          toast.error('Failed to load dashboard data')
          setDashboardData({ overview: {}, users: [] })
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error)
        toast.error('Error loading dashboard data')
        setDashboardData({ overview: {}, users: [] })
      } finally {
        setLoading(false)
      }
    }

    loadDashboardData()
  }, [moduleView])

  // Filter users based on search term
  const filteredUsers = dashboardData?.users?.filter(user => {
    if (!user) return false;
    return (
      (user.userName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.role || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }) || []

  // No dummy data - using real data from API

  // Define columns for the DataTable
  const columns = [
    {
      accessorKey: 'userName',
      header: 'User Name',
      size: 120,
      cell: ({ getValue }) => (
        <span className="fw-medium">{getValue()}</span>
      )
    },
    {
      accessorKey: 'userType',
      header: 'Type',
      size: 80,
      cell: ({ getValue }) => (
        <span className="text-muted">{getValue()}</span>
      )
    },
    {
      accessorKey: 'role',
      header: 'Role',
      size: 100,
      cell: ({ getValue }) => (
        <span>{getValue()}</span>
      )
    },
    {
      accessorKey: 'email',
      header: 'Email',
      size: 150,
      cell: ({ getValue }) => (
        <span className="text-primary">{getValue()}</span>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 80,
      cell: ({ getValue }) => {
        const status = getValue()
        return (
          <span className={`status-badge ${status.toLowerCase()}`}>
            {status?.toUpperCase()}
          </span>
        )
      }
    },
    {
      accessorKey: 'isOnline',
      header: 'Online',
      size: 60,
      cell: ({ getValue }) => {
        const isOnline = getValue()
        return (
          <div className={`status-indicator ${isOnline ? 'online' : 'offline'}`}></div>
        )
      }
    },
    {
      accessorKey: 'lastLogin',
      header: 'Last Login',
      size: 90,
      cell: ({ getValue }) => (
        <span className="text-muted">{getValue()}</span>
      )
    },
    {
      accessorKey: 'ipAddress',
      header: 'IP Address',
      size: 120,
      cell: ({ getValue }) => (
        <span className="font-monospace small ip-address">{getValue()}</span>
      )
    },
    {
      accessorKey: 'device',
      header: 'Device',
      size: 120,
      cell: ({ getValue }) => (
        <span className="text-muted small">{getValue()}</span>
      )
    },
    {
      accessorKey: 'location',
      header: 'Location',
      size: 100,
      cell: ({ getValue }) => (
        <span className="text-muted small">{getValue()}</span>
      )
    },
    {
      accessorKey: 'actions',
      header: '',
      size: 0,
      cell: () => null,
      enableSorting: false,
      enableHiding: false
    }
  ]

  const handleAutoIncrementClick = () => {
    navigate('/settings/auto-increment-master')
  }

  const handleTransactionClick = (path) => () => {
    navigate(`/settings/${path}`)
  }

  const handleGlobalMasterClick = () => {
    console.log('Global Master card clicked!')
    navigate('/settings/global-master')
  }

  const handleExport = async () => {
    try {
      await settingsService.exportUsersToExcel()
      toast.success('Users exported successfully!')
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export users')
    }
  }

  const handleSecurityMasterClick = () => {
    console.log('Security Master card clicked!')
    navigate('/settings/security-master')
  }

  const handleUserMasterClick = () => {
    console.log('User Master card clicked!')
    navigate('/settings/user-master')
  }

  // Show loading state while data is being fetched
  if (loading && moduleView === 'overview') {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <div className="settings-title">
            <h1>Settings</h1>
            <p>Manage your system settings and configurations</p>
          </div>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Settings</h1>
          <p style={{textAlign: 'left', margin: 0}}>Manage your system settings and configurations</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/settings')} />

          {/* Key Metrics Grid */}
           {/* Additional KPI Cards Row */}
           <div className="dashboard-metrics-grid">
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <Users size={16} />
                </div>
                <div className="metric-trend positive">
                  <TrendingUp size={12} />
                  <span>+{dashboardData?.metrics?.recentActivity || 0}</span>
                </div>
              </div>
              <div className="metric-value">{dashboardData?.metrics?.totalUsers || 0}</div>
              <div className="metric-title">Total Users</div>
              <div className="metric-subtitle">All system users</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <Shield size={16} />
                </div>
                <div className="metric-trend positive">
                  <TrendingUp size={12} />
                  <span>+{dashboardData?.metrics?.totalRoles || 0}</span>
                </div>
              </div>
              <div className="metric-value">{dashboardData?.metrics?.totalRoles || 0}</div>
              <div className="metric-title">Total Roles</div>
              <div className="metric-subtitle">Access levels defined</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className="metric-trend neutral">
                  <CheckCircle size={12} />
                  <span>DEPT</span>
                </div>
              </div>
              <div className="metric-value">{dashboardData?.metrics?.totalDepartments || 0}</div>
              <div className="metric-title">Total Departments</div>
              <div className="metric-subtitle">Organizational units</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Database size={16} />
                </div>
                <div className="metric-trend positive">
                  <TrendingUp size={12} />
                  <span>+{dashboardData?.metrics?.recentActivity || 0}</span>
                </div>
              </div>
              <div className="metric-value">{dashboardData?.metrics?.recentActivity || 0}</div>
              <div className="metric-title">Recent Activity</div>
              <div className="metric-subtitle">Last 7 days</div>
            </div>
          </div>

          {/* Settings Overview Table */}
          <DataTable
            data={filteredUsers}
            columns={columns}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            enableSelection={false}
            enablePagination={true}
            pageSize={10}
            className="settings-overview-table"
            title="System Users Overview"
            loading={loading}
            onExport={handleExport}
          />

          {/* KPI Cards Section */}
          <div className="dashboard-metrics-grid">
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <Users size={16} />
                </div>
                {dashboardData?.overview?.activeUsers?.change != null && (
                <div className="metric-trend positive">
                  <TrendingUp size={12} />
                  <span>{dashboardData.overview.activeUsers.change}</span>
                </div>
                )}
              </div>
              <div className="metric-value">{dashboardData?.metrics?.activeUsers || 0}</div>
              <div className="metric-title">Active Users</div>
              <div className="metric-subtitle">{dashboardData?.overview?.activeUsers?.period || 'This month'}</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <Shield size={16} />
                </div>
                <div className="metric-trend positive">
                  <TrendingUp size={12} />
                  <span>{dashboardData?.overview?.securityScore?.change || '+0%'}</span>
                </div>
              </div>
              <div className="metric-value">{dashboardData?.overview?.securityScore?.percentage || '95.0%'}</div>
              <div className="metric-title">Security Score</div>
              <div className="metric-subtitle">{dashboardData?.overview?.securityScore?.period || 'vs last month'}</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className="metric-trend negative">
                  <TrendingDown size={12} />
                  <span>SYSTEM</span>
                </div>
              </div>
              <div className="metric-value">{dashboardData?.overview?.pendingUpdates?.count || 0}</div>
              <div className="metric-title">Pending Updates</div>
              <div className="metric-subtitle">{dashboardData?.overview?.pendingUpdates?.status || 'Requires attention'}</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Database size={16} />
                </div>
                <div className="metric-trend neutral">
                  <CheckCircle size={12} />
                  <span>{dashboardData?.overview?.dataBackup?.status || 'UNAVAILABLE'}</span>
                </div>
              </div>
              <div className="metric-value">
                {dashboardData?.overview?.dataBackup?.percentage ?? '—'}
              </div>
              <div className="metric-title">Data Backup</div>
              <div className="metric-subtitle">
                {dashboardData?.overview?.dataBackup?.lastBackup
                  ? `Last backup: ${dashboardData.overview.dataBackup.lastBackup}`
                  : 'Backup status unavailable'}
              </div>
            </div>
          </div>

         

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Settings Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length || 0} items</span>
              </div>
              <div className="action-list">
                {dashboardData?.actionItems?.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => (
                    <div key={index} className={`action-item ${item.priority || 'medium'}`}>
                      <div className="action-icon"><User size={16} /></div>
                      <div className="action-content">
                        <div className="action-title">{item.title}</div>
                        <div className="action-description">{item.description}</div>
                      </div>
                      <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No action items
                  </div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Recent User Activity</h3>
                <span className="activity-count">{dashboardData?.activities?.length || 0} updates</span>
              </div>
              <div className="activity-list">
                {dashboardData?.activities?.length > 0 ? (
                  dashboardData.activities.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.initials || 'U'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.name}</strong> {activity.action}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No recent activity
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>System Performance</h3>
                <p>{`FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart emptyMessage="No system performance metrics configured" />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>User Role Distribution</h3>
                <p>Current system</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {(() => {
                    const rows = (dashboardData?.metrics?.userTypeDistribution || []).map((r) => ({
                      name: r._id || 'Unknown',
                      count: r.count || 0,
                    }))
                    const total = rows.reduce((sum, r) => sum + r.count, 0)
                    const classes = ['excellent', 'good', 'at-risk', 'churned']
                    if (!rows.length) {
                      return (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                          No user type data
                        </div>
                      )
                    }
                    return rows.map((row, index) => (
                      <div key={row.name} className={`health-segment ${classes[index % classes.length]}`}>
                        <div className="segment-bar" style={{ width: `${total > 0 ? Math.max(4, (row.count / total) * 100) : 0}%` }} />
                        <div className="segment-info">
                          <span className="segment-label">{row.name}</span>
                          <span className="segment-count">{row.count}</span>
                        </div>
                      </div>
                    ))
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="settings-masters-content">
            <div className="settings-masters-header">
              <h2>Settings Masters</h2>
              <p>Manage your system master data and configurations</p>
            </div>
            <div className="settings-masters-grid">
              <div className="settings-master-card" onClick={handleGlobalMasterClick} style={{cursor: 'pointer'}}>
                <div className="settings-master-icon">
                  <Globe size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Global Master</h3>
                  <p>Global system configurations and settings</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card" onClick={handleSecurityMasterClick} style={{cursor: 'pointer'}}>
                <div className="settings-master-icon">
                  <Lock size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Security Master</h3>
                  <p>Security policies and access controls</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <ShoppingCart size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Sales Master</h3>
                  <p>Sales configurations and customer data</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <UserCheck size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>HR Master</h3>
                  <p>Human resources and employee data</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Lightbulb size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>NPD & Planning Master</h3>
                  <p>New product development and planning</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Upload size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Uploads Master</h3>
                  <p>File upload configurations and templates</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Package size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Purchase Master</h3>
                  <p>Purchase orders and vendor management</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Factory size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Production</h3>
                  <p>Production planning and scheduling</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Award size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Quality Master</h3>
                  <p>Quality control and standards</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Tag size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Label Master</h3>
                  <p>Product labeling and barcode management</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Wrench size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Maintenance Master</h3>
                  <p>Equipment maintenance and schedules</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Cog size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Setting Master</h3>
                  <p>System settings and preferences</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <CreditCard size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Finance Master</h3>
                  <p>Financial configurations and accounts</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card">
                <div className="settings-master-icon">
                  <Calculator size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>Accounts Master</h3>
                  <p>Accounting rules and chart of accounts</p>
                  <div className="settings-master-count">—</div>
                </div>
              </div>
              <div className="settings-master-card" onClick={handleUserMasterClick} style={{cursor: 'pointer'}}>
                <div className="settings-master-icon">
                  <User size={24} />
                </div>
                <div className="settings-master-content">
                  <h3>User Master</h3>
                  <p>User accounts and access management</p>
                  <div className="settings-master-count">
                    {dashboardData?.masterData?.users != null
                      ? `${dashboardData.masterData.users} Users`
                      : dashboardData?.metrics?.totalUsers != null
                        ? `${dashboardData.metrics.totalUsers} Users`
                        : '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="settings-transactions-content">
            <div className="settings-transactions-header">
              <h2>Settings Transactions</h2>
              <p>Track and manage all system configuration changes</p>
            </div>
            <div className="settings-transactions-grid">
              <ModuleOverviewCard
                prefix="settings-transaction"
                moduleName="Settings"
                showArrow={false}
                onClick={() => navigate('/settings?view=overview')}
              />
              <div className="settings-transaction-card" onClick={handleAutoIncrementClick} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <Hash size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>Auto Increment</h3>
                  <p>Manage auto-increment sequences and counters</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={handleTransactionClick('application-parameters')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <Sliders size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>Application Parameters</h3>
                  <p>Configure application-level parameters and settings</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={() => navigate('/settings/email-notifications')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <Mail size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>Email Notifications</h3>
                  <p>Event recipients (To / CC / BCC) and the daily digest schedule</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={() => navigate('/settings/export-details')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <Globe size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>Export Details</h3>
                  <p>LUT number, filing date and uploaded LUT document for export invoices</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={() => navigate('/settings/gst-reminder-settings')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <Calculator size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>GST Reminder &amp; Compliance</h3>
                  <p>GST due-date reminders with sales, purchase, ITC and payable working summary</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={handleTransactionClick('audit')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <FileSearch size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>Audit</h3>
                  <p>System audit trails and compliance tracking</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={handleTransactionClick('user-changes')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <User size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>User Changes</h3>
                  <p>User account and permission modifications</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={handleTransactionClick('security-updates')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <Shield size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>Security Updates</h3>
                  <p>Security policy and access control changes</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={handleTransactionClick('config-changes')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <SettingsIcon size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>Config Changes</h3>
                  <p>System configuration modifications</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
              <div className="settings-transaction-card" onClick={handleTransactionClick('data-operations')} style={{ cursor: 'pointer' }}>
                <div className="settings-transaction-icon">
                  <Database size={24} />
                </div>
                <div className="settings-transaction-content">
                  <h3>Data Operations</h3>
                  <p>Data backup and maintenance operations</p>
                  <div className="settings-transaction-count">—</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="settings-reports-content">
            <div className="settings-reports-header">
              <h2>Settings Reports</h2>
              <p>Comprehensive system analytics and reporting</p>
            </div>
            <div className="settings-reports-grid">
              <div className="settings-report-card">
                <div className="settings-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="settings-report-content">
                  <h3>System Performance</h3>
                  <p>System performance and health metrics</p>
                  <div className="settings-report-status">Updated Today</div>
                </div>
              </div>
              <div className="settings-report-card">
                <div className="settings-report-icon">
                  <User size={24} />
                </div>
                <div className="settings-report-content">
                  <h3>User Activity</h3>
                  <p>User activity and access patterns</p>
                  <div className="settings-report-status">Updated Today</div>
                </div>
              </div>
              <div className="settings-report-card">
                <div className="settings-report-icon">
                  <Shield size={24} />
                </div>
                <div className="settings-report-content">
                  <h3>Security Reports</h3>
                  <p>Security events and compliance reports</p>
                  <div className="settings-report-status">Updated Today</div>
                </div>
              </div>
              <div className="settings-report-card">
                <div className="settings-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="settings-report-content">
                  <h3>Audit Reports</h3>
                  <p>System audit trails and change logs</p>
                  <div className="settings-report-status">Updated Today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render nested routes */}
      <Outlet />
    </div>
  )
}

export default Settings
